import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import { IsOfflineSupplierNoShare } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import ActionCell from 'src/sections/manage_suppliers/table_cell/ActionCell';
import ProgramInfoCell from 'src/sections/manage_suppliers/table_cell/ProgramInfoCell';
import SupplierEmailCell from 'src/sections/manage_suppliers/table_cell/SupplierEmailCell';
import SupplierInfoCell from 'src/sections/manage_suppliers/table_cell/SupplierInfoCell';
import SupplierTextCell from 'src/sections/manage_suppliers/table_cell/SupplierTextCell';
import TransmissionTypeStatusFilterMenu from 'src/sections/manage_suppliers/table_cell/TransmissionTypeStatusFilterMenu';
import { SupplierInfoEditPermissions } from 'src/sections/manage_suppliers/utils/supplierInfoPermissions';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';

const buildSupplierInfoColumn = (title: string, locked: boolean): GridColumn<ManageSupplier> => ({
  field: 'supplierInfo',
  title,
  component: SupplierInfoCell,
  locked,
  show: true,
  width: 120,
  minResizableWidth: 120,
  menu: false,
  sortable: false,
  filterable: false,
  dataType: 'fixed',
});

export const useSupplierColumns = (): GridColumn<ManageSupplier>[] => {
  const { t } = useTranslation();
  const canEditSupplierInfo = usePermission({
    permissions: SupplierInfoEditPermissions,
    or: true,
  });
  const isOfflineSupplierNoShare = usePermission({
    permissions: IsOfflineSupplierNoShare,
  });
  const showCreatedBy = !isOfflineSupplierNoShare;

  return useMemo(() => {
    const supplierInfoColumn = buildSupplierInfoColumn(t('Supplier Info'), canEditSupplierInfo);

    return [
      {
        field: 'actionType',
        title: t('Action'),
        component: ActionCell,
        locked: true,
        show: true,
        width: 140,
        minResizableWidth: 140,
        menu: false,
        sortable: false,
        filterable: false,
        dataType: 'fixed',
      },
      {
        field: 'supplierName',
        title: t('Supplier Name'),
        component: SupplierTextCell,
        locked: true,
        show: true,
        width: 180,
        minResizableWidth: 180,
        filter: 'text',
        dataType: 'string',
      },
      ...(canEditSupplierInfo ? [supplierInfoColumn] : []),
      {
        field: 'catalog',
        title: t('Catalog'),
        component: SupplierTextCell,
        locked: true,
        show: true,
        width: 150,
        minResizableWidth: 150,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'transmissionTypeStatus',
        title: t('Order Type'),
        width: 180,
        filter: 'text',
        columnMenu: TransmissionTypeStatusFilterMenu,
        dataType: 'string',
      },
      ...(showCreatedBy
        ? [
            {
              field: 'createdBy',
              title: t('Created By'),
              width: 180,
              filter: 'text',
              dataType: 'string',
            } as GridColumn<ManageSupplier>,
          ]
        : []),
      {
        field: 'apId',
        title: t('AP ID'),
        width: 120,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'externalSupplierName',
        title: t('External Supplier Name'),
        width: 210,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'accountNumber',
        title: t('Account #'),
        width: 120,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'email',
        title: t('Email'),
        component: SupplierEmailCell,
        show: true,
        width: 180,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'contactName',
        title: t('Contact Name'),
        width: 150,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'address',
        title: t('Address'),
        width: 150,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'city',
        title: t('City'),
        width: 120,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'stateProvince',
        title: t('State'),
        width: 130,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'zipPostalCode',
        title: t('Zip'),
        width: 130,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'country',
        title: t('Country'),
        width: 120,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'phone',
        title: t('Phone'),
        width: 130,
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'programInfo',
        title: t('Program Info'),
        component: ProgramInfoCell,
        width: 120,
        menu: false,
        sortable: false,
        filterable: false,
        dataType: 'fixed',
      },
      ...(!canEditSupplierInfo ? [{ ...supplierInfoColumn, locked: false }] : []),
    ];
  }, [canEditSupplierInfo, showCreatedBy, t]);
};

export default useSupplierColumns;
